import React from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, Upload, Clock, MessageCircle, Mail, ShieldCheck } from "lucide-react";

/**
 * Post-signup landing for doctors whose application is `status: pending`.
 * Reached via Signup.jsx redirect after a successful doctor signup.
 */
export default function DoctorOnboarding() {
  const { state } = useLocation();
  const name = state?.name || "Doctor";
  const email = state?.email || "";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold tracking-wider uppercase mb-5">
          <CheckCircle2 className="w-3.5 h-3.5" /> Application submitted
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
          Welcome to NeuroOpinion India, Dr. {name}.
        </h1>
        <p className="mt-3 text-slate-600 max-w-xl">
          Your application is now with our admin team. We verify every MCI / NMC registration personally — it usually takes <b>24-48 working hours</b>.
        </p>

        <Card className="mt-8 p-6 border-slate-200">
          <h2 className="font-heading text-lg font-semibold text-slate-900">What happens next</h2>
          <ol className="mt-4 space-y-4">
            <Step n="1" icon={Clock} title="We review your MCI / NMC registration"
              desc="Our admin team cross-checks your registration number with the Medical Council database. Usually done within 24 hours on business days."/>
            <Step n="2" icon={Upload} title="Send us your medical licence"
              desc="Please reply to your confirmation email with a clear photo or PDF of your MCI / NMC licence, or WhatsApp it to the number below. You can also upload it from the doctor dashboard once your account is activated."/>
            <Step n="3" icon={Mail} title="Activation email"
              desc={email ? `Once approved, you'll get an activation email at ${email}. Sign in at /login to set your consultation fee, bio and photo — and start receiving cases.` : "Once approved, you'll get an activation email. Sign in at /login to set your consultation fee, bio and photo — and start receiving cases."}/>
          </ol>
        </Card>

        <Card className="mt-6 p-6 border-teal-200 bg-gradient-to-br from-white to-teal-50/40">
          <div className="flex items-center gap-2 text-teal-800">
            <ShieldCheck className="w-5 h-5"/>
            <h3 className="font-heading font-semibold text-base">While you wait…</h3>
          </div>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Get a 10-minute head-start by WhatsApp-ing us your license + a recent professional photo + a short bio. Your account will be ready to accept cases the moment approval lands.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a
              href="https://wa.me/918317674160?text=Hi%2C%20I%27m%20a%20new%20doctor%20applicant%20on%20NeuroOpinion%20India.%20Here%27s%20my%20licence."
              target="_blank"
              rel="noreferrer"
              data-testid="onboarding-whatsapp-cta"
            >
              <Button className="h-11 px-5 bg-green-600 hover:bg-green-700 text-white">
                <MessageCircle className="w-4 h-4 mr-2"/> Send documents on WhatsApp
              </Button>
            </a>
            <Link to="/login" data-testid="onboarding-login-link">
              <Button variant="outline" className="h-11 px-5">Back to login</Button>
            </Link>
          </div>
        </Card>

        <div className="mt-10 text-center text-sm text-slate-500">
          Questions? Email{" "}
          <a className="text-teal-700 font-semibold hover:underline" href="mailto:onboarding@neuroopinion.in">onboarding@neuroopinion.in</a>
          {" "}or call{" "}
          <a className="text-teal-700 font-semibold hover:underline" href="tel:+918317674160">+91 83176 74160</a>.
        </div>
      </div>
    </div>
  );
}

function Step({ n, icon: Icon, title, desc }) {
  return (
    <li className="flex items-start gap-4">
      <div className="w-9 h-9 rounded-full bg-teal-700 text-white grid place-items-center font-heading font-bold shrink-0">
        {n}
      </div>
      <div className="flex-1">
        <div className="font-semibold text-slate-900 flex items-center gap-2">
          <Icon className="w-4 h-4 text-teal-700"/> {title}
        </div>
        <p className="mt-1 text-sm text-slate-600 leading-relaxed">{desc}</p>
      </div>
    </li>
  );
}
